import { useState } from 'preact/hooks';
import { type PaymentEntry } from '@khata/core';
import {
  Empty,
  MonthPicker,
  PageHead,
  Row,
  SearchBox,
  ShowMore,
  VoidedToggle,
  periodLabel,
} from '../components/index.js';
import { dShort, taka } from '../lib/format.js';
import { t } from '../lib/i18n.js';
import { entries, master, state, today } from '../lib/khata.js';
import { accountName, partyName } from '../lib/labels.js';
import { listEntries, matches, monthsWithEntries, periodRange } from '../lib/lists.js';
import { usePaged } from '../lib/paging.js';
import { listPeriod, openSheet, showVoided } from '../lib/ui.js';

export function Payments() {
  const s = state.value;
  const m = master.value;
  const [q, setQ] = useState('');
  const pick = (e: { type: string }): e is PaymentEntry => e.type === 'payment';
  const range = periodRange(listPeriod.value, today.value);
  const inPeriod = listEntries(s, entries.value, pick, showVoided.value, range);
  const rows = q.trim()
    ? inPeriod.filter(({ entry: e }) =>
        matches(`${partyName(m, e.partyId)} ${accountName(m, e.accountId)} ${e.note}`, q),
      )
    : inPeriod;
  const live = rows.filter((r) => !r.voided);
  const inSum = live
    .filter((r) => r.entry.direction === 'in')
    .reduce((tt, r) => tt + r.entry.amount, 0);
  const outSum = live
    .filter((r) => r.entry.direction === 'out')
    .reduce((tt, r) => tt + r.entry.amount, 0);
  const paged = usePaged(rows, listPeriod.value.kind === 'all' ? 300 : 1000);

  return (
    <>
      <PageHead
        title={t('payments_title')}
        sub={`${t('payments_period', { in: taka(inSum as never), out: taka(outSum as never) })} · ${periodLabel()}`}
      >
        <button
          type="button"
          class="btn btn-primary"
          onClick={() => openSheet({ kind: 'payment', direction: 'in' })}
        >
          {t('payments_receive')}
        </button>
        <button
          type="button"
          class="btn btn-secondary"
          onClick={() => openSheet({ kind: 'payment', direction: 'out' })}
        >
          {t('payments_pay')}
        </button>
      </PageHead>
      <SearchBox value={q} onInput={setQ} placeholder={t('search_payments')} />
      <MonthPicker months={monthsWithEntries(entries.value)} />
      <VoidedToggle />
      <div class="list">
        {rows.length === 0 && <Empty>{t('payments_empty')}</Empty>}
        {paged.visible.map(({ entry: e, voided }) => (
          <Row
            key={e.id}
            title={`${t(e.direction === 'in' ? 'payments_from' : 'payments_to')} ${partyName(m, e.partyId)}`}
            sub={`${dShort(e.date)} · ${accountName(m, e.accountId)}${e.note ? ' · ' + e.note : ''}`}
            amount={(e.direction === 'in' ? '+' : '−') + taka(e.amount)}
            amountTone={voided ? 'muted' : e.direction === 'in' ? 'good' : 'ink'}
            amountSub={voided ? t('voided') : undefined}
            strike={voided}
            onClick={() => openSheet({ kind: 'entry', id: e.id })}
          />
        ))}
      </div>
      <ShowMore shown={paged.shown} total={paged.total} onMore={paged.more} />
    </>
  );
}
